import { AccountError, type CommerceSource } from '@akademi/site-builder-runtime';

/**
 * Applying for a trade account: a business asks for trade prices, the shop reviews the request,
 * and the shopper hears by email. Any shop can offer it; each shop switches it on for itself.
 *
 * <p>Akademi's `CommerceSource` has no trade accounts. A host that offers them adds the
 * {@link TradeApplicationMethods} below, and a block reads them as optional, the way the saved
 * lists are read: a host without them shows the block's placeholder.
 */

/** What the business does, as the shop's backend names it. */
export const TRADE_BUSINESS_TYPES = ['INSTALLER', 'ELECTRICIAN', 'BUILDER', 'DESIGNER', 'RETAILER', 'OTHER'] as const;
export type TradeBusinessType = (typeof TRADE_BUSINESS_TYPES)[number];

/** One application, as the host sends it. */
export interface TradeApplicationRequest {
  businessName: string;
  businessType: TradeBusinessType;
  contactName: string;
  /** Required from a guest. A signed-in shopper's application goes under their account's address. */
  email?: string;
  phone: string;
  companyNumber?: string;
  vatNumber?: string;
  /** Several lines. */
  businessAddress?: string;
  website?: string;
  message?: string;
  /** The language the shop's emails about it are written in. */
  language?: 'en' | 'tr';
  /** The address as lines; the backend joins them into `businessAddress` itself. */
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  postcode?: string;
  /** A sole trader's UTR, instead of a company number. */
  taxReference?: string;
  /** `false` is the answer "not VAT registered". */
  vatRegistered?: boolean;
  tradeBody?: string;
  tradeBodyNumber?: string;
  references?: TradeReferenceRequest[];
  /** The applicant confirmed the details are true and may be checked. */
  declared?: boolean;
}

export interface TradeReferenceRequest {
  company: string;
  contactName: string;
  email?: string;
  phone?: string;
  relationship?: string;
}

/** What a shop may require of an applicant, beyond the business and how to reach it. */
export const TRADE_REQUIREMENTS = ['COMPANY_OR_TAX_REFERENCE', 'VAT', 'TRADE_BODY', 'PROOF', 'REFERENCES'] as const;
export type TradeRequirement = (typeof TRADE_REQUIREMENTS)[number];

/** What a document proves, as the backend names it. */
export const TRADE_DOCUMENT_TYPES = [
  'COMPANY_REGISTRATION',
  'VAT_REGISTRATION',
  'TRADE_BODY_MEMBERSHIP',
  'TRADE_INVOICE',
  'PROOF_OF_ADDRESS',
  'OTHER_PROOF',
] as const;
export type TradeDocumentType = (typeof TRADE_DOCUMENT_TYPES)[number];

/** What this shop asks for, so the form can say all of it before the applicant starts. */
export interface TradeRequirements {
  required: TradeRequirement[];
  /** How many trade references; 0 when none are asked for. */
  references: number;
  /** Bodies to offer in a list; empty means a free-text box. */
  tradeBodies: string[];
  documentTypes: TradeDocumentType[];
  maxDocuments: number;
  /** Each file at most this large. */
  maxBytes: number;
  /**
   * What a trade account saves, worked out by the shop from its own prices for the band approval
   * gives. Null (or absent) when the shop does not show it, or has too few prices to say.
   */
  savings?: TradeSavings | null;
}

/**
 * The saving a trade band gives over retail, computed by the shop from its real prices: the
 * median saving and the 90th percentile, both rounded down to a whole percent, over `products`.
 */
export interface TradeSavings {
  band: string;
  products: number;
  typicalPct: number;
  upToPct: number;
}

/** One login on a trade account, shown to the account's admin. */
export interface TradeAccountMember {
  name: string | null;
  email: string;
  /** `buyer`, `approver`, `finance` or `admin`. */
  role: string;
}

/** An approved trade account, as its own user sees it. */
export interface TradeAccountSummary {
  accountNumber: string;
  company: string;
  band: string;
  /** This user's role on the account: `buyer`, `approver`, `finance` or `admin`. */
  role: string;
  /** Decimal strings, in `currency`. */
  creditLimit: string | null;
  termsDays: number | null;
  /** `DAYS_FROM_INVOICE`, `END_OF_MONTH`, `PROFORMA` or `CASH`. */
  termsType: string | null;
  minOrderValue: string | null;
  currency: string;
  onStop: boolean;
  /** Only when staff wrote one. */
  onStopReason: string | null;
  vatExempt: boolean;
  /** ISO-8601 instant. */
  approvedAt: string | null;
  /** Only for the account's admin; empty for everyone else. */
  members: TradeAccountMember[];
  savings: TradeSavings | null;
}

/** A saving the page can state honestly: both figures whole and positive, over some products. */
export function tradeSavingsOf(raw: unknown): TradeSavings | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Partial<TradeSavings>;
  const whole = (n: unknown) => typeof n === 'number' && Number.isFinite(n) && n > 0;
  if (!whole(value.typicalPct) || !whole(value.upToPct) || !whole(value.products)) return null;
  return {
    band: typeof value.band === 'string' ? value.band : 'TRADE',
    products: Math.floor(value.products as number),
    typicalPct: Math.floor(value.typicalPct as number),
    upToPct: Math.max(Math.floor(value.upToPct as number), Math.floor(value.typicalPct as number)),
  };
}

/** What a host that cannot say asks for: nothing beyond the basics, within the backend's own limits. */
export const NO_TRADE_REQUIREMENTS: TradeRequirements = {
  required: [],
  references: 0,
  tradeBodies: [],
  documentTypes: [...TRADE_DOCUMENT_TYPES],
  maxDocuments: 8,
  maxBytes: 26_214_400,
};

/** One file to send, and what it proves. */
export interface TradeDocumentUpload {
  type: TradeDocumentType;
  file: File;
}

export type TradeApplicationState = 'PENDING' | 'APPROVED' | 'REJECTED';

/** Where an application stands. */
export interface TradeApplicationStatus {
  status: TradeApplicationState;
  businessName: string;
  email: string;
  /** ISO-8601 instant. */
  submittedAt: string;
  decidedAt: string | null;
  /** Only when it was refused and the shop chose to say why. */
  reason: string | null;
  /**
   * Pending and still waiting for the proof documents the shop requires: an application made with
   * the sign-up, which cannot carry files (contract §7). The shopper uploads them signed in.
   */
  documentsOutstanding?: boolean;
}

/** What a host adds to its `CommerceSource` for trade accounts. */
export interface TradeApplicationMethods {
  /** Whether this shop takes applications at all. Off unless the shop has switched it on. */
  tradeApplicationsEnabled(): Promise<boolean>;
  /** With documents, sent as a multipart form; without, as JSON. */
  submitTradeApplication(
    input: TradeApplicationRequest,
    documents?: TradeDocumentUpload[],
  ): Promise<TradeApplicationStatus>;
  /** What this shop asks of an applicant. A host without it asks for nothing more. */
  tradeApplicationRequirements?(): Promise<TradeRequirements>;
  /** The signed-in shopper's application; null when they have none or nobody is signed in. */
  myTradeApplication(): Promise<TradeApplicationStatus | null>;
  /**
   * The approved trade account the signed-in shopper is a user of; null when they are not one,
   * or nobody is signed in. A host without it shows only where the application stands.
   */
  myTradeAccount?(): Promise<TradeAccountSummary | null>;
  /**
   * The proof documents the signed-in shopper's pending application still lacks — one made with
   * the sign-up. A host without it cannot take them after the application; its forms send them
   * with the application instead.
   */
  uploadTradeDocuments?(documents: TradeDocumentUpload[]): Promise<TradeApplicationStatus>;
}

export type TradeCommerceSource = CommerceSource & Partial<TradeApplicationMethods>;

/**
 * Whether the shop takes applications, as far as this host can say.
 *
 * <p>A host that cannot say (no `tradeApplicationsEnabled`) is taken at its word that it offers
 * them, and the backend's `NOT_FOUND` settles it. A host that says so, but cannot find out, is
 * taken as off: the feature is off unless a shop has switched it on.
 */
export async function tradeApplicationsOn(commerce: TradeCommerceSource): Promise<boolean> {
  if (!commerce.tradeApplicationsEnabled) return true;
  try {
    return (await commerce.tradeApplicationsEnabled()) === true;
  } catch {
    return false;
  }
}

/** The backend's limits, which the form's fields hold to. */
export const TRADE_FIELD_LIMITS = {
  businessName: 300,
  contactName: 200,
  email: 200,
  phone: 40,
  companyNumber: 40,
  vatNumber: 40,
  businessAddress: 500,
  website: 300,
  message: 2000,
  addressLine1: 200,
  addressLine2: 200,
  city: 100,
  postcode: 20,
  taxReference: 40,
  tradeBody: 100,
  tradeBodyNumber: 60,
  relationship: 200,
} as const;

/** One reference as typed. */
export interface TradeReferenceDraft {
  company: string;
  contactName: string;
  email: string;
  phone: string;
  relationship: string;
}

export const EMPTY_REFERENCE_DRAFT: TradeReferenceDraft = {
  company: '',
  contactName: '',
  email: '',
  phone: '',
  relationship: '',
};

/** The form as typed. */
export interface TradeApplicationDraft {
  businessName: string;
  businessType: TradeBusinessType | '';
  contactName: string;
  email: string;
  phone: string;
  companyNumber: string;
  vatNumber: string;
  businessAddress: string;
  website: string;
  message: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
  taxReference: string;
  /** `''` not answered, `'yes'` registered, `'no'` not VAT registered. */
  vatRegistered: '' | 'yes' | 'no';
  tradeBody: string;
  tradeBodyNumber: string;
  declared: boolean;
}

/** What a problem can name: a field of the draft, or the references or documents as a whole. */
export type TradeDraftField = keyof TradeApplicationDraft | 'references' | 'documents';

export const EMPTY_TRADE_DRAFT: TradeApplicationDraft = {
  businessName: '',
  businessType: '',
  contactName: '',
  email: '',
  phone: '',
  companyNumber: '',
  vatNumber: '',
  businessAddress: '',
  website: '',
  message: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postcode: '',
  taxReference: '',
  vatRegistered: '',
  tradeBody: '',
  tradeBodyNumber: '',
  declared: false,
};

/** Why the form cannot be sent yet. */
export type TradeDraftProblem =
  | { kind: 'MISSING'; fields: TradeDraftField[] }
  | { kind: 'INVALID_EMAIL' }
  | { kind: 'REFERENCES'; needed: number }
  | { kind: 'REFERENCE_INCOMPLETE'; index: number }
  | { kind: 'TOO_MANY_FILES'; max: number }
  | { kind: 'FILE_TOO_LARGE'; name: string };

const EMAIL = /^[^@\s\\]+@[^@\s\\]+$/;

/** A reference with anything typed in it; one left blank is not sent. */
function started(reference: TradeReferenceDraft): boolean {
  return Object.values(reference).some((value) => value.trim() !== '');
}

/**
 * What is missing or wrong, checked here so the shopper hears it before the round trip; the
 * server checks it all again, against the same requirements. The website is not checked: a shop
 * can read `example.com` as well as `https://example.com`.
 *
 * <p>A shop with any requirement runs the full form, so it also needs the address lines and the
 * declaration, as the server does.
 */
export function tradeDraftProblem(
  draft: TradeApplicationDraft,
  options: {
    emailRequired: boolean;
    requirements?: TradeRequirements;
    references?: TradeReferenceDraft[];
    documents?: TradeDocumentUpload[];
    /**
     * The application goes with a sign-up, which cannot carry files: required proof is uploaded
     * once the account is made, so its absence here is not a problem.
     */
    documentsLater?: boolean;
  },
): TradeDraftProblem | null {
  const requirements = options.requirements ?? NO_TRADE_REQUIREMENTS;
  const needs = (requirement: TradeRequirement) => requirements.required.includes(requirement);
  const fullForm = requirements.required.length > 0;
  const references = options.references ?? [];
  const documents = options.documents ?? [];

  const required: Array<Exclude<keyof TradeApplicationDraft, 'declared' | 'vatRegistered'>> = [
    'businessName',
    'businessType',
    'contactName',
    'phone',
  ];
  if (options.emailRequired) required.splice(3, 0, 'email');
  if (fullForm) required.push('addressLine1', 'city', 'postcode');
  const missing: TradeDraftField[] = required.filter((field) => !draft[field].trim());
  if (needs('COMPANY_OR_TAX_REFERENCE') && !draft.companyNumber.trim() && !draft.taxReference.trim()) {
    missing.push('companyNumber');
  }
  if (needs('VAT') && !draft.vatRegistered) missing.push('vatRegistered');
  if (draft.vatRegistered === 'yes' && !draft.vatNumber.trim()) missing.push('vatNumber');
  const hasBody = draft.tradeBody.trim() !== '';
  const hasNumber = draft.tradeBodyNumber.trim() !== '';
  if (needs('TRADE_BODY') || hasBody || hasNumber) {
    if (!hasBody) missing.push('tradeBody');
    if (!hasNumber) missing.push('tradeBodyNumber');
  }
  if (needs('PROOF') && documents.length === 0 && !options.documentsLater) missing.push('documents');
  if (fullForm && !draft.declared) missing.push('declared');
  if (missing.length) return { kind: 'MISSING', fields: missing };

  if (options.emailRequired && !EMAIL.test(draft.email.trim())) return { kind: 'INVALID_EMAIL' };
  const incomplete = references.findIndex(
    (reference) =>
      started(reference) &&
      (!reference.company.trim() ||
        !reference.contactName.trim() ||
        (!reference.email.trim() && !reference.phone.trim())),
  );
  if (incomplete >= 0) return { kind: 'REFERENCE_INCOMPLETE', index: incomplete };
  if (needs('REFERENCES') && references.filter(started).length < requirements.references) {
    return { kind: 'REFERENCES', needed: requirements.references };
  }
  if (documents.length > requirements.maxDocuments) return { kind: 'TOO_MANY_FILES', max: requirements.maxDocuments };
  const large = documents.find((document) => document.file.size > requirements.maxBytes);
  if (large) return { kind: 'FILE_TOO_LARGE', name: large.file.name };
  return null;
}

/**
 * The application to send: every field trimmed, the email lower-cased, blank optional fields
 * left out. A signed-in shopper's email is their account's.
 */
export function tradeApplicationRequest(
  draft: TradeApplicationDraft,
  options: { accountEmail?: string | null; language?: string | null; references?: TradeReferenceDraft[] },
): TradeApplicationRequest {
  const email = (options.accountEmail ?? draft.email).trim().toLowerCase();
  const request: TradeApplicationRequest = {
    businessName: draft.businessName.trim(),
    businessType: draft.businessType as TradeBusinessType,
    contactName: draft.contactName.trim(),
    ...(email ? { email } : {}),
    phone: draft.phone.trim(),
  };
  for (const name of [
    'companyNumber',
    'vatNumber',
    'businessAddress',
    'website',
    'message',
    'addressLine1',
    'addressLine2',
    'city',
    'postcode',
    'taxReference',
    'tradeBody',
    'tradeBodyNumber',
  ] as const) {
    const value = draft[name].trim();
    if (value) request[name] = value;
  }
  if (draft.vatRegistered === 'no') {
    // "Not VAT registered" wins over a number typed before the answer changed.
    request.vatRegistered = false;
    delete request.vatNumber;
  } else if (draft.vatRegistered === 'yes') {
    request.vatRegistered = true;
  }
  const references = (options.references ?? []).filter(started).map((reference) => {
    const out: TradeReferenceRequest = {
      company: reference.company.trim(),
      contactName: reference.contactName.trim(),
    };
    const email = reference.email.trim().toLowerCase();
    if (email) out.email = email;
    if (reference.phone.trim()) out.phone = reference.phone.trim();
    if (reference.relationship.trim()) out.relationship = reference.relationship.trim();
    return out;
  });
  if (references.length) request.references = references;
  if (draft.declared) request.declared = true;
  const language = (options.language ?? '').slice(0, 2).toLowerCase();
  if (language === 'en' || language === 'tr') request.language = language;
  return request;
}

/** The refusals the form has its own words for; anything else reads as `UNAVAILABLE`. */
export const TRADE_ERROR_KINDS = [
  'APPLICATION_OPEN',
  'ALREADY_TRADE',
  'INVALID_INPUT',
  'RATE_LIMITED',
  'NOT_SIGNED_IN',
  'NOT_FOUND',
  'INVALID_FILE',
  'UNAVAILABLE',
] as const;
export type TradeErrorKind = (typeof TRADE_ERROR_KINDS)[number];

/** A refusal's kind. `NOT_FOUND` is a shop that takes no applications. */
export function tradeErrorKind(error: unknown): TradeErrorKind {
  const kind: string = error instanceof AccountError ? error.kind : 'UNAVAILABLE';
  return (TRADE_ERROR_KINDS as readonly string[]).includes(kind) ? (kind as TradeErrorKind) : 'UNAVAILABLE';
}

/**
 * What the shop asks for, as far as this host can say. A host without the call, or one that
 * cannot answer just now, asks for nothing beyond the basics; the server still has the last word.
 * Values this form does not know are dropped rather than shown as raw names.
 */
export async function tradeRequirementsOf(commerce: TradeCommerceSource): Promise<TradeRequirements> {
  if (!commerce.tradeApplicationRequirements) return NO_TRADE_REQUIREMENTS;
  let answer: Partial<TradeRequirements> | null;
  try {
    answer = await commerce.tradeApplicationRequirements();
  } catch {
    return NO_TRADE_REQUIREMENTS;
  }
  const required = (answer?.required ?? []).filter((value): value is TradeRequirement =>
    (TRADE_REQUIREMENTS as readonly string[]).includes(value),
  );
  const documentTypes = (answer?.documentTypes ?? []).filter((value): value is TradeDocumentType =>
    (TRADE_DOCUMENT_TYPES as readonly string[]).includes(value),
  );
  const count = (value: unknown, fallback: number) =>
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;
  return {
    required,
    references: required.includes('REFERENCES') ? count(answer?.references, 1) : 0,
    tradeBodies: (answer?.tradeBodies ?? []).filter((body) => typeof body === 'string' && body.trim() !== ''),
    documentTypes: documentTypes.length ? documentTypes : [...TRADE_DOCUMENT_TYPES],
    maxDocuments: count(answer?.maxDocuments, NO_TRADE_REQUIREMENTS.maxDocuments),
    maxBytes: count(answer?.maxBytes, NO_TRADE_REQUIREMENTS.maxBytes),
    savings: tradeSavingsOf(answer?.savings),
  };
}

/** Lives beside `safeNext`, which it is built from; re-exported here for the trade form. */
export { signInHrefFor } from './accountShared';
